import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { useCompare, CompareInfluencer } from '@/contexts/CompareContext';
import { Info, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface MetricConfig {
  key: string;
  label: string;
  type: 'avatar' | 'text' | 'number' | 'percent' | 'badge';
  tooltip?: string;
  highlight?: boolean;
}

const METRICS: MetricConfig[] = [
  { key: 'profile', label: 'Profile', type: 'avatar' },
  { key: 'name', label: 'Name', type: 'text' },
  { key: 'handle', label: 'Handle', type: 'text' },
  { key: 'location', label: 'Location', type: 'text', tooltip: 'Primary city and country' },
  { key: 'category', label: 'Category', type: 'text' },
  { key: 'tier', label: 'Tier', type: 'badge' },
  { key: 'followers', label: 'Followers', type: 'number', tooltip: 'Total followers across platforms', highlight: true },
  { key: 'avgLikes', label: 'Avg Likes', type: 'number', tooltip: 'Average likes per post', highlight: true },
  { key: 'avgViews', label: 'Avg Views', type: 'number', tooltip: 'Average views per video', highlight: true },
  { key: 'engagementRate', label: 'Engagement Rate', type: 'percent', tooltip: 'Engagement rate = (likes + comments) / followers', highlight: true },
  { key: 'reach', label: 'Reach', type: 'number', tooltip: 'Estimated audience reach', highlight: true },
];

interface ExtractedMetrics {
  profile: string | null | undefined;
  name: string;
  handle: string;
  location: string;
  category: string;
  tier: string;
  followers: number | null | undefined;
  avgLikes: number | null | undefined;
  avgViews: number | null | undefined;
  engagementRate: number | null | undefined;
  reach: number | null | undefined;
}

const extractMetrics = (influencer: CompareInfluencer): ExtractedMetrics => {
  const platforms = influencer.platforms || {};
  const platformKeys = Object.keys(platforms);
  const platformData = platformKeys.length > 0 ? (platforms as Record<string, any>)[platformKeys[0]] : null;
  const metrics = platformData?.metrics || {};

  return {
    profile: influencer.profile_pic_path,
    name: influencer.name,
    handle: `@${influencer.primary_handle}`,
    location: [influencer.city, influencer.country].filter(Boolean).join(', ') || '—',
    category: influencer.categories?.[0] || '—',
    tier: influencer.tier || '—',
    followers: influencer.total_followers_count,
    avgLikes: metrics.avg_likes,
    avgViews: metrics.avg_views ? metrics.avg_views * 2.65 : null,
    engagementRate: influencer.avg_engagement_rate,
    reach: metrics.estimated_reach,
  };
};

const formatNumber = (value: number | null | undefined): string => {
  if (value == null) return '—';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toString();
};

const formatPercent = (value: number | null | undefined): string => {
  if (value == null) return '—';
  return `${value.toFixed(1)}%`;
};

const getTierColor = (tier: string): string => {
  const tierLower = tier.toLowerCase();
  switch (tierLower) {
    case 'mega': return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'macro': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'micro': return 'bg-green-100 text-green-700 border-green-200';
    case 'nano': return 'bg-amber-100 text-amber-700 border-amber-200';
    default: return 'bg-muted text-muted-foreground';
  }
};

const getHighlightClass = (
  value: number | null | undefined,
  allValues: (number | null | undefined)[],
): string => {
  const validValues = allValues.filter((v): v is number => v != null && v > 0);
  if (validValues.length < 2 || value == null || value <= 0) return '';

  const max = Math.max(...validValues);
  const min = Math.min(...validValues);

  if (value === max) return 'bg-green-100 text-green-700 font-semibold';
  if (value === min && validValues.length > 1) return 'bg-red-50 text-red-400';
  return '';
};

export const CompareModal: React.FC = () => {
  const { selectedInfluencers, isCompareOpen, setIsCompareOpen, removeFromCompare } = useCompare();

  const extractedData = useMemo(() => {
    return selectedInfluencers.map(extractMetrics);
  }, [selectedInfluencers]);

  const getValueHighlight = (metricKey: string, index: number): string => {
    const metric = METRICS.find(m => m.key === metricKey);
    if (!metric?.highlight) return '';

    const allValues = extractedData.map(d => d[metricKey as keyof ExtractedMetrics] as number | null | undefined);
    const currentValue = extractedData[index]?.[metricKey as keyof ExtractedMetrics] as number | null | undefined;

    return getHighlightClass(currentValue, allValues);
  };

  const renderCellValue = (metric: MetricConfig, data: ExtractedMetrics, index: number) => {
    const value = data[metric.key as keyof ExtractedMetrics];
    const highlightClass = getValueHighlight(metric.key, index);

    switch (metric.type) {
      case 'avatar':
        return (
          <div className="flex flex-col items-center gap-2">
            <Avatar className="h-16 w-16">
              <AvatarImage src={value as string || ''} alt={data.name} />
              <AvatarFallback className="bg-primary/10 text-primary text-lg">
                {data.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              className="h-6 text-xs text-muted-foreground hover:text-destructive"
              onClick={() => removeFromCompare(selectedInfluencers[index].id)}
            >
              <X className="h-3 w-3 mr-1" />
              Remove
            </Button>
          </div>
        );
      case 'badge':
        return (
          <Badge variant="outline" className={getTierColor(value as string)}>
            {(value as string).charAt(0).toUpperCase() + (value as string).slice(1)}
          </Badge>
        );
      case 'number':
        return (
          <span className={`px-2 py-1 rounded ${highlightClass}`}>
            {formatNumber(value as number | null | undefined)}
          </span>
        );
      case 'percent':
        return (
          <span className={`px-2 py-1 rounded ${highlightClass}`}>
            {formatPercent(value as number | null | undefined)}
          </span>
        );
      default:
        return <span className="text-foreground">{value as string}</span>;
    }
  };

  const renderMetricHeader = (metric: MetricConfig) => {
    if (metric.tooltip) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1 cursor-help">
              <span>{metric.label}</span>
              <Info className="h-3 w-3 text-muted-foreground" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p>{metric.tooltip}</p>
          </TooltipContent>
        </Tooltip>
      );
    }
    return <span>{metric.label}</span>;
  };

  if (selectedInfluencers.length === 0) return null;

  return (
    <Dialog open={isCompareOpen} onOpenChange={setIsCompareOpen}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col select-none" onContextMenu={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Compare Influencers ({selectedInfluencers.length})
          </DialogTitle>
        </DialogHeader>

        <TooltipProvider delayDuration={200}>
          <div className="overflow-auto flex-1 -mx-6 px-6">
            <Table>
              <TableHeader>
                <TableRow className="border-b">
                  <TableHead className="sticky left-0 bg-background z-10 min-w-[140px] font-semibold">
                    Metric
                  </TableHead>
                  {selectedInfluencers.map((inf) => (
                    <TableHead
                      key={inf.id}
                      className="text-center min-w-[140px] font-semibold"
                    >
                      {inf.name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {METRICS.map((metric) => (
                  <TableRow key={metric.key} className="border-b border-border/50">
                    <TableCell className="sticky left-0 bg-background z-10 font-medium text-muted-foreground">
                      {renderMetricHeader(metric)}
                    </TableCell>
                    {extractedData.map((data, index) => (
                      <TableCell key={selectedInfluencers[index].id} className="text-center">
                        {renderCellValue(metric, data, index)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TooltipProvider>

        <div className="flex items-center justify-between pt-4 border-t mt-4">
          <p className="text-sm text-muted-foreground">
            <span className="inline-block w-3 h-3 rounded bg-green-100 mr-1"></span> Best value
            <span className="inline-block w-3 h-3 rounded bg-red-50 ml-3 mr-1"></span> Lowest value
          </p>
          <Button variant="outline" type="button" onClick={() => setIsCompareOpen(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompareModal;
